/**
 * Refonte Achats · Stock WP-13 (03_DATABASE DB-12) — the searchable fields of a product and of a
 * nomenclature line, and the `search_text` key built from them. One definition, read by the server
 * (written at every product / nomenclature write, back-fill) and by the offline store (which rebuilds
 * the key when a snapshot row carries none).
 *
 * Accepts the Prisma row shape (`dci: { nom }`, `forme: { libelle }`, `laboratoire: { nom }`,
 * `codes_barre: [{ code, deleted_at? }]`) and the flat shape (`dci_nom`, `forme_libelle`,
 * `laboratoire_nom`, `codes: []`).
 */
import { normaliserRecherche } from "./normalise.js";

const nomDe = (rel, ...plats) => {
  if (rel && typeof rel === "object") return rel.nom ?? rel.libelle ?? null;
  if (typeof rel === "string") return rel;
  for (const p of plats) if (p != null && p !== "") return p;
  return null;
};

/** The live box barcodes of a product row: every non-deleted `codes_barre[].code`, then the mirror. */
export function codesDuProduit(p) {
  const out = [];
  const ajouter = (c) => {
    const v = c == null ? "" : String(c).trim();
    if (v && !out.includes(v)) out.push(v);
  };
  for (const c of Array.isArray(p?.codes_barre) ? p.codes_barre : []) {
    if (c && typeof c === "object") {
      if (!c.deleted_at) ajouter(c.code);
    } else ajouter(c);
  }
  for (const c of Array.isArray(p?.codes) ? p.codes : []) ajouter(c);
  ajouter(p?.code_barre);
  return out;
}

/**
 * Product fields in DB-12 order; `cle` is the name returned in `pertinence.champs`.
 * @type {ReadonlyArray<{ cle: string, lire: (p: any) => unknown }>}
 */
export const CHAMPS_PRODUIT = Object.freeze([
  { cle: "nom_commercial", lire: (p) => p?.nom_commercial },
  { cle: "dci", lire: (p) => nomDe(p?.dci, p?.dci_nom) },
  { cle: "laboratoire", lire: (p) => nomDe(p?.laboratoire, p?.laboratoire_nom) },
  { cle: "marque", lire: (p) => p?.marque },
  { cle: "forme", lire: (p) => nomDe(p?.forme, p?.forme_libelle) },
  { cle: "dosage", lire: (p) => p?.dosage },
  { cle: "conditionnement", lire: (p) => p?.conditionnement },
  { cle: "num_enr_cnas", lire: (p) => p?.num_enr_cnas },
]);

/** Nomenclature fields in DB-12 order (nom_marque, dci, forme, dosage, conditionnement, laboratoire, N° ENR, code). */
export const CHAMPS_NOMENCLATURE = Object.freeze([
  { cle: "nom_marque", lire: (n) => n?.nom_marque },
  { cle: "dci", lire: (n) => n?.dci },
  { cle: "forme", lire: (n) => n?.forme },
  { cle: "dosage", lire: (n) => n?.dosage },
  { cle: "conditionnement", lire: (n) => n?.conditionnement },
  { cle: "laboratoire", lire: (n) => n?.laboratoire },
  { cle: "num_enregistrement", lire: (n) => n?.num_enregistrement },
  { cle: "code", lire: (n) => n?.code },
]);

/**
 * The normalised value of every non-empty field (codes under `codes_barre`, one entry per code).
 * @returns {Array<{ cle: string, valeur: string }>}
 */
export function champsNormalises(row, { nomenclature = false } = {}) {
  const out = [];
  for (const ch of nomenclature ? CHAMPS_NOMENCLATURE : CHAMPS_PRODUIT) {
    const v = normaliserRecherche(ch.lire(row));
    if (v) out.push({ cle: ch.cle, valeur: v });
  }
  if (!nomenclature) {
    for (const c of codesDuProduit(row)) {
      const v = normaliserRecherche(c);
      if (v) out.push({ cle: "codes_barre", valeur: v });
    }
  }
  return out;
}

const joindre = (champs) => {
  const vus = [];
  for (const { valeur } of champs) if (!vus.includes(valeur)) vus.push(valeur);
  return vus.length ? vus.join(" ") : null;
};

/** `Produit.search_text` (DB-12); null when the product has no searchable value. */
export function cleRechercheProduit(produit) {
  return joindre(champsNormalises(produit));
}

/** `NomenclatureProduit.search_text` (DB-12); null when the line has no searchable value. */
export function cleRechercheNomenclature(ligne) {
  return joindre(champsNormalises(ligne, { nomenclature: true }));
}
